import React from 'react'
import ReactMarkdown from 'react-markdown'
import { HashLink } from 'react-router-hash-link';
import Site from '../site'

import { Download, Image } from '../components'
import WavTable from '../wavtable'

import '../css/site.css'


/*****************************************************************************
Constants
*****************************************************************************/


const NAME = 'voiceblock';


/*****************************************************************************
Project website
*****************************************************************************/


export default function VoiceBlock() {
  return (
    <Site name={NAME}>
      <TableOfContents />
      <Listening />
      <Controls />
    </Site>);
}


/*****************************************************************************
Utilities
*****************************************************************************/


function Controls() {
  let markdown = `### Controlling filter parameters with VoiceBlock\n` +
    `We demonstrate how VoiceBlock performs an attack by the magnitudes of ` +
    `a filter bank of 128 filters. We provide three examples, including ` +
    `plots of the time-varying magnitude responses determined by VoiceBlock ` +
    `for each filter. Filter bands are arranged vertically by ` +
    `frequency, and the frequency magnitude response of each band over time ` +
    `is represented by the corresponding curve. The thin grey line beneath ` +
    `each curve represents a "neutral" frequency magnitude response of 1, ` +
    `with deviations above and below this line representing boosts and cuts, ` +
    `respectively. Note that the controls vary smoothly, and often exhibit ` +
    `sparseness in quiet regions of the waveform.`;
  let alt = 'The visualized filter controls';
  let indices = ['11', '12', '18'];
  let examples = indices.map((index, i) =>
    <Example
      alt={alt}
      columns={['Clean', 'VoiceBlock']}
      experiment={`control-${index}`}
      index={index}
      key={i}
      width={900}
      displayColumns={['Original', 'VoiceBlock']}
    />
  );
  return (
    <div className='controls section'>
      <section id='controls'>
        <ReactMarkdown>{markdown}</ReactMarkdown>
        {examples}
      </section>
    </div>
  );
}


function Example({ alt, columns, experiment, index, width, displayColumns }) {
  index = index.toString();
  return (
    <div style={{ 'paddingBottom': '3.5rem' }}>
      <Image
        name={NAME}
        file={`${experiment}.png`}
        alt={alt}
        width={width}
      />
      <WavTable
        name={NAME}
        experiment={experiment}
        extension={'mp3'}
        rows={['_']}
        columns={columns}
        displayColumns={displayColumns}
      />
    </div>
  );
}


function Listening() {
  let markdown = `## Listening examples\n` +
    `We provide five examples of clean audio with and without the four ` +
    `attacks used in our subjective evaluation. All attack examples ` +
    `shown here are successful in evading the ResNetSE34V2 system ` +
    `described in section 4.1 of the paper. We also provide a download ` +
    `link for our subjective evaluation materials, including configuration ` +
    `files, evaluation files, and results.`;
  let alt = 'The spectrograms corresponding to the audio files';
  let columns = ['Noise', 'Spectral Gating', 'Universal', 'VoiceBlock', 'Clean'];
  let indices = ['12', '18', '39', '40', '88'];
  let examples = indices.map((index, i) =>
    <Example
      alt={alt}
      columns={columns}
      experiment={`listening-${index}`}
      index={index}
      key={i}
      width={1000}
    />
  );
  return (
    <div className='listening-examples'>
      <section id='listening-examples'>
        <ReactMarkdown>{markdown}</ReactMarkdown>
        {examples}
        <Download
          url={'https://drive.google.com/drive/folders/1ualNK6wy19Z5XGp1Cgb_dliwraAKqAfB?usp=sharing'}
          text={'Download subjective evaluation files and results'}
        />
      </section>
    </div>
  );
}


function TableOfContents() {
  return (
    <div className='section'>
      <ReactMarkdown>{`## Table of Contents`}</ReactMarkdown>
      <ul>
        <li>
          <HashLink to="/#listening-examples">Listening examples</HashLink>
        </li>
        <li>
          <HashLink to="/#controls">
            Controlling filter parameters with VoiceBlock
          </HashLink>
        </li>
        <li>
          <HashLink to="/#citation">How to cite this work</HashLink>
        </li>
      </ul>
    </div>
  );
}
